import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { getDashboardStats } from '@/services/firestore'
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts'

// ======================== Types ========================
interface DashStats {
    todayOrders: number
    todayRevenue: number
    activeOrders: number
    lowStockCount: number
    pendingFbr: number
    receivable: number
    payable: number
    totalRevenue: number
    totalExpenses?: number
    monthlySales: Array<{ month: string, value: number }>
    monthlyPurchases?: Array<{ month: string, value: number }>
}

// ======================== Component ========================
export function DashboardView() {
    const { profile } = useAuth()
    const [stats, setStats] = useState<DashStats | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => { loadStats() }, [])

    const loadStats = async () => {
        try {
            const data = await getDashboardStats()
            setStats(data)
        } catch (err) {
            console.error('Stats error:', err)
        } finally {
            setLoading(false)
        }
    }

    const f = (n: number) => n.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

    const today = new Date().toLocaleDateString('en-PK', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' })

    // ======================== Dashboard Data ========================
    // Currently initializing to zero until backend aggregation is complete
    const revenue = stats?.todayRevenue || 0
    const salesYTD = stats?.totalRevenue || 0
    const expenses = stats?.totalExpenses || 0
    const profit = salesYTD - expenses

    const bankAccounts = [
        { name: 'Cash on hand', code: '230901', balance: revenue },
        { name: 'Current account 1', code: '230902', balance: 0 },
        { name: 'Current account 2', code: '230903', balance: 0 },
        { name: 'Savings account 1', code: '230904', balance: 0 },
        { name: 'Savings account 2', code: '230905', balance: 0 },
    ]

    const inventoryStatus = [
        { label: 'Low Stock', count: stats?.lowStockCount || 1, color: '#f59e0b' },
        { label: 'Out of Stock', count: 0, color: '#ef4444' },
        { label: 'Oversold', count: 1, color: '#dc2626' },
        { label: 'In Stock', count: 12, color: '#22c55e' },
    ]

    const defaultMonthlySales = [
        { month: 'Aug', value: 0 },
        { month: 'Sep', value: 0 },
        { month: 'Oct', value: 0 },
        { month: 'Nov', value: 0 },
        { month: 'Dec', value: 0 },
        { month: 'Jan', value: 0 },
        { month: 'Feb', value: revenue || 0 },
    ]
    const monthlySales = stats?.monthlySales?.length ? stats.monthlySales : defaultMonthlySales
    const maxSale = Math.max(...monthlySales.map(s => s.value), 1)
    const barColors = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#8b5cf6']

    const defaultMonthlyPurchases = [
        { month: 'Aug', value: 0 },
        { month: 'Sep', value: 0 },
        { month: 'Oct', value: 0 },
        { month: 'Nov', value: 0 },
        { month: 'Dec', value: 0 },
        { month: 'Jan', value: 0 },
        { month: 'Feb', value: 0 },
    ]
    const purchaseData = stats?.monthlyPurchases?.length ? stats.monthlyPurchases : defaultMonthlyPurchases

    if (loading) {
        return (
            <div className="p-4 pt-14 lg:pt-6 lg:p-6 bg-[#f0f5ff] min-h-screen animate-pulse">
                <div className="flex items-center justify-between mb-4">
                    <div><div className="h-5 w-48 bg-slate-200 rounded-lg mb-2" /><div className="h-3 w-32 bg-slate-200 rounded" /></div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                    {[1, 2, 3].map(i => <div key={i} className="bg-white rounded-xl h-72 border border-slate-200/60" />)}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    {[1, 2, 3].map(i => <div key={i} className="bg-white rounded-xl h-44 border border-slate-200/60" />)}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map(i => <div key={i} className="h-28 rounded-xl bg-slate-200" />)}
                </div>
            </div>
        )
    }

    return (
        <div className="p-4 pt-14 lg:pt-6 lg:p-6 bg-[#f0f5ff] min-h-screen">
            {/* Top Bar */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-xl font-bold text-slate-800">
                        Welcome back, <span className="text-slate-900">{profile?.displayName || 'Admin'}</span> 👋
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">{today}</p>
                </div>
            </div>

            {/* Row 1: Receivable, Payable, Bank Balances */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                {/* Sales Overview Graph */}
                <div className="bg-white rounded-2xl border border-slate-200/60 p-5 hover:shadow-xl hover:shadow-blue-500/5 transition-all duration-300 flex flex-col">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <h3 className="text-base font-bold text-slate-800">Sales Overview</h3>
                            <p className="text-xs text-slate-500 mt-1">Monthly sales performance</p>
                        </div>
                        <div className="bg-blue-50 text-blue-600 text-[10px] font-bold px-2.5 py-1 rounded-md">
                            +12.5%
                        </div>
                    </div>
                    <div className="flex-1 w-full mt-2 h-[200px] min-h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={monthlySales} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4}/>
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#94a3b8' }} dy={10} />
                                <Tooltip 
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                                    formatter={(value: number) => [`PKR ${f(value)}`, 'Sales']}
                                />
                                <Area type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorSales)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Purchases Overview Graph */}
                <div className="bg-white rounded-2xl border border-slate-200/60 p-5 hover:shadow-xl hover:shadow-emerald-500/5 transition-all duration-300 flex flex-col">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <h3 className="text-base font-bold text-slate-800">Purchases Overview</h3>
                            <p className="text-xs text-slate-500 mt-1">Monthly inventory expenses</p>
                        </div>
                        <div className="bg-emerald-50 text-emerald-600 text-[10px] font-bold px-2.5 py-1 rounded-md">
                            -4.2%
                        </div>
                    </div>
                    <div className="flex-1 w-full mt-2 h-[200px] min-h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={purchaseData} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#94a3b8' }} dy={10} />
                                <Tooltip 
                                    cursor={{fill: '#f1f5f9'}}
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                                    formatter={(value: number) => [`PKR ${f(value)}`, 'Purchases']}
                                />
                                <Bar dataKey="value" fill="#10b981" radius={[4, 4, 0, 0]} barSize={16}>
                                    {purchaseData.map((_, index) => (
                                        <Cell key={`cell-${index}`} fill={index === purchaseData.length - 1 ? '#10b981' : '#6ee7b7'} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Bank Balances + Products */}
                <div className="space-y-4">
                    {/* Bank Balances */}
                    <div className="bg-white rounded-xl border border-slate-200/60 p-4 hover:shadow-md transition-all duration-300">
                        <h3 className="text-sm font-bold text-slate-700 mb-2">Bank Balances</h3>
                        <table className="w-full text-[10px]">
                            <thead>
                                <tr className="text-slate-400 border-b border-slate-100">
                                    <td className="pb-1 font-semibold">Bank</td>
                                    <td className="text-right pb-1 font-semibold">Balance</td>
                                </tr>
                            </thead>
                            <tbody>
                                {bankAccounts.map((acc, i) => (
                                    <tr key={i} className="text-slate-600 border-b border-slate-50">
                                        <td className="py-1">{acc.name} ({acc.code})</td>
                                        <td className="text-right py-1 font-medium">PKR {f(acc.balance)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Products Summary */}
                    <div className="bg-white rounded-xl border border-slate-200/60 p-4 hover:shadow-md transition-all duration-300">
                        <h3 className="text-sm font-bold text-slate-700 mb-2">Products</h3>
                        <table className="w-full text-[11px]">
                            <thead>
                                <tr className="text-slate-400">
                                    <td className="pb-1">Status</td>
                                    <td className="text-right pb-1">Quantity</td>
                                </tr>
                            </thead>
                            <tbody>
                                {inventoryStatus.map((inv, i) => (
                                    <tr key={i} className="border-b border-slate-50">
                                        <td className="py-1.5 text-slate-700 font-medium">{inv.label}</td>
                                        <td className="text-right py-1.5 font-bold" style={{ color: inv.color }}>{inv.count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Row 2: Sales, Expenses, Profit & Loss */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {/* Sales */}
                <div className="bg-white rounded-xl border border-slate-200/60 p-4 hover:shadow-md transition-all duration-300">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-bold text-slate-700">Sales</h3>
                        <span className="text-[10px] text-slate-400 bg-slate-50 px-2 py-0.5 rounded">This Financial Year</span>
                    </div>
                    <div className="text-2xl font-extrabold text-slate-900 mb-3">{f(salesYTD)}</div>
                    {/* Mini bar chart */}
                    <div className="flex items-end gap-1 h-16">
                        {monthlySales.map((s, i) => (
                            <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                                <div
                                    className="w-full rounded-t"
                                    style={{
                                        height: `${(s.value / maxSale) * 100}%`,
                                        minHeight: '3px',
                                        background: barColors[i % barColors.length],
                                    }}
                                />
                                <span className="text-[8px] text-slate-400">{s.month}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Expenses */}
                <div className="bg-white rounded-xl border border-slate-200/60 p-4 hover:shadow-md transition-all duration-300">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-bold text-slate-700">Expenses</h3>
                        <span className="text-[10px] text-slate-400 bg-slate-50 px-2 py-0.5 rounded">This Financial Year</span>
                    </div>
                    <div className="text-2xl font-extrabold text-slate-900">{f(expenses)}</div>
                    <div className="mt-3 flex items-end gap-1 h-16">
                        {monthlySales.map((_, i) => (
                            <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                                <div className="w-full rounded-t bg-slate-100" style={{ height: '3px' }} />
                                <span className="text-[8px] text-slate-400">{monthlySales[i].month}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Profit & Loss */}
                <div className="bg-white rounded-xl border border-slate-200/60 p-4 hover:shadow-md transition-all duration-300">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-bold text-slate-700">Profit & Loss</h3>
                        <span className="text-[10px] text-slate-400 bg-slate-50 px-2 py-0.5 rounded">This Financial Year</span>
                    </div>
                    <div className="mb-2">
                        <div className="text-[10px] text-slate-400 mb-0.5">Net Profit</div>
                        <div className="text-2xl font-extrabold text-emerald-600">{f(profit)}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-slate-100">
                        <div>
                            <div className="text-[10px] text-slate-400">Total Income</div>
                            <div className="text-sm font-bold text-slate-900">{f(salesYTD)}</div>
                        </div>
                        <div>
                            <div className="text-[10px] text-slate-400">Total Expenses</div>
                            <div className="text-sm font-bold text-slate-900">{f(expenses)}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Row 3: Daily Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-red-500 to-rose-600 rounded-xl p-4 text-white shadow-lg shadow-red-500/20 hover:shadow-xl hover:shadow-red-500/30 hover:-translate-y-0.5 transition-all duration-300">
                    <div className="text-[10px] uppercase tracking-wider opacity-80 mb-1">Daily Revenue</div>
                    <div className="text-xl font-extrabold">{f(revenue)}</div>
                    <div className="mt-2 text-[10px] opacity-70">
                        <div>Cash Sale: {f(revenue)}</div>
                        <div>Credit Sale: {f(0)}</div>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-4 text-white shadow-lg shadow-green-500/20 hover:shadow-xl hover:shadow-green-500/30 hover:-translate-y-0.5 transition-all duration-300">
                    <div className="text-[10px] uppercase tracking-wider opacity-80 mb-1">Today Orders</div>
                    <div className="text-xl font-extrabold">{stats?.todayOrders || 0}</div>
                    <div className="mt-2 text-[10px] opacity-70">
                        <div>Completed: {stats?.todayOrders || 0}</div>
                        <div>Pending: {stats?.activeOrders || 0}</div>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-4 text-white shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/30 hover:-translate-y-0.5 transition-all duration-300">
                    <div className="text-[10px] uppercase tracking-wider opacity-80 mb-1">FBR Invoices</div>
                    <div className="text-xl font-extrabold">{stats?.pendingFbr || 0}</div>
                    <div className="mt-2 text-[10px] opacity-70">
                        <div>Pending: {stats?.pendingFbr || 0}</div>
                        <div>Submitted: 0</div>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/30 hover:-translate-y-0.5 transition-all duration-300">
                    <div className="text-[10px] uppercase tracking-wider opacity-80 mb-1">Low Stock</div>
                    <div className="text-xl font-extrabold">{stats?.lowStockCount || 0}</div>
                    <div className="mt-2 text-[10px] opacity-70">
                        <div>Out of Stock: 0</div>
                        <div>Reorder Needed: {stats?.lowStockCount || 0}</div>
                    </div>
                </div>
            </div>
        </div>
    )
}
